/*!
 * TimbricaMidiWriter — minimal Standard MIDI File Type-1 writer.
 * Builds a single-track .mid from recorded note events.
 *
 * Input: events = [{ tMs, type:'on'|'off', midi, velocity }] sorted ascending by tMs.
 *        bpm — tempo (default 120).
 *        channel — MIDI channel 0-15 (default 0). Pass 9 for GM percussion
 *                  (channel 10, 1-indexed) so drum exports trigger drum kits
 *                  in a DAW instead of a melodic instrument on channel 1.
 *
 * Output: Uint8Array ready to wrap in a Blob.
 *
 * ES5-safe, no dependencies.
 */
window.TimbricaMidiWriter = (function () {
    'use strict';

    var TICKS_PER_QUARTER = 480;

    function writeVLQ(value) {
        var out = [];
        var buf = value & 0x7F;
        value >>= 7;
        while (value > 0) {
            buf <<= 8;
            buf |= ((value & 0x7F) | 0x80);
            value >>= 7;
        }
        while (true) {
            out.push(buf & 0xFF);
            if (buf & 0x80) buf >>= 8;
            else break;
        }
        return out;
    }

    function pushUint32(arr, n) {
        arr.push((n >>> 24) & 0xFF, (n >>> 16) & 0xFF, (n >>> 8) & 0xFF, n & 0xFF);
    }
    function pushUint16(arr, n) {
        arr.push((n >>> 8) & 0xFF, n & 0xFF);
    }

    function build(events, bpm, channel) {
        bpm = bpm || 120;
        var ch = (channel || 0) & 0x0F;
        var msPerTick = 60000 / bpm / TICKS_PER_QUARTER;

        /* Convert events to (tick, bytes) entries */
        var ticked = [];
        for (var i = 0; i < events.length; i++) {
            var e = events[i];
            var tick = Math.round(e.tMs / msPerTick);
            var status = (e.type === 'on' ? 0x90 : 0x80) | ch;
            var vel = e.type === 'on' ? Math.max(1, Math.round((e.velocity || 0.6) * 127)) : 0;
            ticked.push({ tick: tick, bytes: [status, e.midi, vel] });
        }
        ticked.sort(function (a, b) { return a.tick - b.tick; });

        /* Build track body: delta + bytes for each event, then EOT */
        var trackBody = [];

        /* Tempo meta at tick 0 */
        var usPerQuarter = Math.round(60000000 / bpm);
        var deltaTempo = writeVLQ(0);
        for (var j = 0; j < deltaTempo.length; j++) trackBody.push(deltaTempo[j]);
        trackBody.push(0xFF, 0x51, 0x03,
            (usPerQuarter >> 16) & 0xFF,
            (usPerQuarter >> 8) & 0xFF,
            usPerQuarter & 0xFF);

        var lastTick = 0;
        for (var k = 0; k < ticked.length; k++) {
            var delta = ticked[k].tick - lastTick;
            lastTick = ticked[k].tick;
            var deltaBytes = writeVLQ(delta);
            for (var b = 0; b < deltaBytes.length; b++) trackBody.push(deltaBytes[b]);
            for (var c = 0; c < ticked[k].bytes.length; c++) trackBody.push(ticked[k].bytes[c]);
        }

        /* End of track meta */
        trackBody.push(0x00, 0xFF, 0x2F, 0x00);

        /* Build MThd header */
        var header = [];
        for (var h = 0; h < 4; h++) header.push('MThd'.charCodeAt(h));
        pushUint32(header, 6);    /* header length */
        pushUint16(header, 1);    /* format 1 */
        pushUint16(header, 1);    /* num tracks */
        pushUint16(header, TICKS_PER_QUARTER);

        /* MTrk chunk */
        var trkHead = [];
        for (var h2 = 0; h2 < 4; h2++) trkHead.push('MTrk'.charCodeAt(h2));
        pushUint32(trkHead, trackBody.length);

        /* Concatenate */
        var total = header.length + trkHead.length + trackBody.length;
        var out = new Uint8Array(total);
        var pos = 0;
        for (var x = 0; x < header.length; x++) out[pos++] = header[x];
        for (var y = 0; y < trkHead.length; y++) out[pos++] = trkHead[y];
        for (var z = 0; z < trackBody.length; z++) out[pos++] = trackBody[z];
        return out;
    }

    /**
     * buildMulti(tracks, bpm) → Uint8Array
     *
     * Format-1 file with a conventional tempo track followed by one MTrk per
     * part, which is what a DAW expects when it offers to split parts onto
     * separate instruments. build() above stays byte-for-byte as it was — four
     * tools ship on it — so this is an addition, not a rewrite.
     *
     * tracks: [{ events, channel, name }]
     *   events  — same shape as build(): {tMs, type:'on'|'off', midi, velocity}
     *   channel — 0-15; pass 9 for GM percussion
     *   name    — optional, written as an FF 03 track-name meta
     *
     * Empty tracks are dropped rather than written as silent stubs: a chiptune
     * with no drums should not open with a dead percussion track.
     */
    function buildMulti(tracks, bpm) {
        bpm = bpm || 120;
        var msPerTick = 60000 / bpm / TICKS_PER_QUARTER;

        var chunks = [];

        // ── Track 0: tempo map only, no notes (the format-1 convention) ──
        var tempoBody = [];
        var usPerQuarter = Math.round(60000000 / bpm);
        pushBytes(tempoBody, writeVLQ(0));
        tempoBody.push(0xFF, 0x51, 0x03,
            (usPerQuarter >> 16) & 0xFF, (usPerQuarter >> 8) & 0xFF, usPerQuarter & 0xFF);
        tempoBody.push(0x00, 0xFF, 0x2F, 0x00);
        chunks.push(tempoBody);

        for (var t = 0; t < tracks.length; t++) {
            var tr = tracks[t] || {};
            var evs = tr.events || [];
            if (!evs.length) continue;
            var ch = (tr.channel || 0) & 0x0F;

            var ticked = [];
            for (var i = 0; i < evs.length; i++) {
                var e = evs[i];
                var status = (e.type === 'on' ? 0x90 : 0x80) | ch;
                var vel = e.type === 'on' ? Math.max(1, Math.round((e.velocity || 0.6) * 127)) : 0;
                ticked.push({ tick: Math.round(e.tMs / msPerTick), on: e.type === 'on', bytes: [status, e.midi, vel] });
            }
            // Note-offs before note-ons at the same tick, so a repeated pitch
            // does not silence the note that just started.
            ticked.sort(function (a, b) { return (a.tick - b.tick) || ((a.on ? 1 : 0) - (b.on ? 1 : 0)); });

            var body = [];
            if (tr.name) {
                pushBytes(body, writeVLQ(0));
                body.push(0xFF, 0x03, tr.name.length & 0x7F);
                for (var n = 0; n < tr.name.length && n < 127; n++) body.push(tr.name.charCodeAt(n) & 0x7F);
            }
            var lastTick = 0;
            for (var k = 0; k < ticked.length; k++) {
                pushBytes(body, writeVLQ(ticked[k].tick - lastTick));
                lastTick = ticked[k].tick;
                pushBytes(body, ticked[k].bytes);
            }
            body.push(0x00, 0xFF, 0x2F, 0x00);
            chunks.push(body);
        }

        var header = [];
        for (var h = 0; h < 4; h++) header.push('MThd'.charCodeAt(h));
        pushUint32(header, 6);
        pushUint16(header, 1);              /* format 1 */
        pushUint16(header, chunks.length);  /* tempo track + parts */
        pushUint16(header, TICKS_PER_QUARTER);

        var total = header.length;
        for (var c0 = 0; c0 < chunks.length; c0++) total += 8 + chunks[c0].length;

        var out = new Uint8Array(total), pos = 0;
        for (var x = 0; x < header.length; x++) out[pos++] = header[x];
        for (var c1 = 0; c1 < chunks.length; c1++) {
            out[pos++] = 0x4D; out[pos++] = 0x54; out[pos++] = 0x72; out[pos++] = 0x6B;  /* MTrk */
            var len = chunks[c1].length;
            out[pos++] = (len >>> 24) & 0xFF; out[pos++] = (len >>> 16) & 0xFF;
            out[pos++] = (len >>> 8) & 0xFF;  out[pos++] = len & 0xFF;
            for (var z = 0; z < len; z++) out[pos++] = chunks[c1][z];
        }
        return out;
    }

    function pushBytes(arr, src) { for (var i = 0; i < src.length; i++) arr.push(src[i]); }

    /**
     * notesToEvents(notes) → on/off event pairs.
     * Takes {start, dur, midi, amp} (seconds) — the shape the chiptune voicer
     * produces — and returns the {tMs,type,...} pairs the builders consume.
     */
    function notesToEvents(notes) {
        var out = [];
        for (var i = 0; i < notes.length; i++) {
            var n = notes[i];
            var start = (n.start != null ? n.start : n.startTimeSeconds) || 0;
            var dur = (n.dur != null ? n.dur : n.durationSeconds) || 0;
            var midi = (n.midi != null ? n.midi : n.pitchMidi) | 0;
            if (!(dur > 0) || midi <= 0 || midi > 127) continue;
            out.push({ tMs: start * 1000, type: 'on', midi: midi, velocity: n.amp != null ? n.amp : 0.7 });
            out.push({ tMs: (start + dur) * 1000, type: 'off', midi: midi, velocity: 0 });
        }
        return out;
    }

    return { build: build, buildMulti: buildMulti, notesToEvents: notesToEvents };
})();
